import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Button } from '@/components/ui';

/**
 * Keeps one broken page from blanking the site.
 *
 * React unmounts the entire tree when a render throws, so without this a single bad
 * field read — `entry.roles.some(...)` where `roles` is undefined because the payload
 * nests the user one level deeper — produces a white screen with nothing to click and
 * nothing on screen to report. That is what this is here for; it happened.
 *
 * Wrapped around the `Outlet` rather than the whole app so the header and navigation
 * survive and the user can leave the page that broke.
 *
 * Class component because there is still no hook for this, and the labels arrive as
 * props rather than from `useTranslation` for the same reason.
 */
interface Props {
  children: ReactNode;
  /** Changing this clears the error — the route path, so navigating away recovers. */
  resetKey: string;
  title: string;
  hint: string;
  retryLabel: string;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidUpdate(prev: Props) {
    if (prev.resetKey !== this.props.resetKey && this.state.error) {
      this.setState({ error: null });
    }
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    // The stack is the only record of what happened; a user reporting "it went white"
    // has nothing else to send.
    console.error('page crashed', error, info.componentStack);
  }

  override render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div className="mx-auto max-w-lg py-8">
        <h1 className="mb-2 text-lg font-semibold">{this.props.title}</h1>
        <p className="mb-4 text-sm text-muted-foreground">{this.props.hint}</p>
        <pre className="mb-4 overflow-x-auto rounded border border-border bg-muted/40 p-3 text-xs">
          {error.message}
        </pre>
        <Button onClick={() => this.setState({ error: null })}>{this.props.retryLabel}</Button>
      </div>
    );
  }
}
